import React, { useContext, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Form, Button, Alert } from "react-bootstrap";
import loginImage from "../../assest/loginimg.jpg";
import {
  sendRegisterOtp,
  registerWithEmail,
  loginWithEmail,
} from "./services/authService";
import { AuthContext } from "../Context/AuthContext";
import { saveToken } from "./utils/tokenUtils";

const getErr = (err, fallback) => err.response?.data?.error || fallback;

const EmailLogin = ({ isModal = false, onSuccess } = {}) => {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState("login"); // 'login' | 'register' | 'verifyOtp'
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const finishLogin = (token, user) => {
    const userData = {
      name: user?.name || "",
      email: user?.email || email,
      mobile: user?.mobile || "",
      userRegId: user?.userRegId,
      role: user?.role,
    };
    saveToken(token, "customer", userData);
    login(userData, token);
    setMessage("Login successful!");
    setTimeout(() => {
      onSuccess?.();
      if (!isModal) {
        navigate("/");
      } else {
        // Logging in via the header modal can happen on any page (flight/hotel/bus results,
        // etc). AuthContext updates reactively, but several older parts of this codebase read
        // auth state once from localStorage on mount rather than reactively — re-navigating to
        // the exact current URL forces route-level components to re-evaluate against the now
        // logged-in state, without a jarring full page reload.
        navigate(`${location.pathname}${location.search}`, { replace: true });
      }
    }, 800);
  };

  const handleLogin = async () => {
    setError("");
    setMessage("");
    if (!email || !password) return setError("Please enter email and password");

    setLoading(true);
    try {
      const { data } = await loginWithEmail({ email: email.trim().toLowerCase(), password });
      finishLogin(data.token, data.user);
    } catch (err) {
      setError(getErr(err, "Login failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    setError("");
    setMessage("");
    if (!name.trim()) return setError("Please enter your name");
    if (!email) return setError("Please enter your email");
    if (!password || password.length < 6) return setError("Password must be at least 6 characters");

    setLoading(true);
    try {
      await sendRegisterOtp({ email: email.trim().toLowerCase() });
      setMode("verifyOtp");
      setMessage(`OTP sent to ${email}`);
    } catch (err) {
      setError(getErr(err, "Failed to send OTP. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndRegister = async () => {
    setError("");
    setMessage("");
    if (!otp) return setError("Please enter the OTP");

    setLoading(true);
    try {
      const { data } = await registerWithEmail({
        email: email.trim().toLowerCase(),
        otp,
        password,
        name: name.trim(),
      });
      finishLogin(data.token, data.user);
    } catch (err) {
      setError(getErr(err, "Registration failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={isModal ? "" : "login-wrapper"}>
      <div className={isModal ? "" : "login-box d-flex flex-column flex-md-row"}>
        {!isModal && (
          <div className="col-12 col-md-6 left-panel p-3 p-md-4 order-2 order-md-1">
            <h4 className="fw-bold text-white bg-danger py-2 px-3 rounded-pill d-inline-block mb-3">
              Registration Benefits:
            </h4>
            <ul className="list-unstyled mt-3">
              <li className="mb-2">✅ No Registration Fees</li>
              <li className="mb-2">✅ Easy Steps for registration</li>
              <li className="mb-2">✅ Manage Your Bookings</li>
              <li className="mb-2">✅ Print Ticket or Voucher</li>
              <li className="mb-2">✅ 24*7 Dedicated Support</li>
              <li className="mb-2">✅ Your Profile is Saved Forever</li>
            </ul>
            <div className="text-center d-none d-md-block">
              <img src={loginImage} alt="Login" className="img-fluid w-75 rounded mt-4" />
            </div>
          </div>
        )}

        <div
          className={
            isModal
              ? "right-panel d-flex flex-column justify-content-center p-4"
              : "col-12 col-md-6 right-panel d-flex flex-column justify-content-center p-3 p-md-4 order-1 order-md-2"
          }
        >
          <h2 className="login-title mb-3 text-center fs-4 fs-md-2">
            {mode === "login" && "Login"}
            {mode === "register" && "Create Your Account"}
            {mode === "verifyOtp" && "Verify Your Email"}
          </h2>

          {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
          {message && <Alert variant="success" className="mb-3">{message}</Alert>}

          <Form>
            {mode === "register" && (
              <Form.Group className="mb-3">
                <Form.Label className="text-light">Full Name</Form.Label>
                <Form.Control value={name} onChange={(e) => setName(e.target.value)} />
              </Form.Group>
            )}

            {["login", "register"].includes(mode) && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label className="text-light">Email</Form.Label>
                  <Form.Control type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="text-light">Password</Form.Label>
                  <Form.Control type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
                </Form.Group>
              </>
            )}

            {mode === "verifyOtp" && (
              <Form.Group className="mb-3">
                <Form.Label className="text-light">Enter the OTP sent to {email}</Form.Label>
                <Form.Control value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} maxLength={6} />
              </Form.Group>
            )}

            {mode === "login" && (
              <>
                <Button onClick={handleLogin} disabled={loading} className="w-100 login-btn mb-2">
                  {loading ? "Signing in…" : "Login"}
                </Button>
                <Button variant="link" className="w-100 text-light text-center" onClick={() => { setMode("register"); setError(""); setMessage(""); }}>
                  New User? Create Account
                </Button>
              </>
            )}

            {mode === "register" && (
              <>
                <Button onClick={handleSendOtp} disabled={loading} className="w-100 login-btn mb-2">
                  {loading ? "Sending OTP…" : "Send OTP"}
                </Button>
                <Button variant="link" className="w-100 text-light text-center" onClick={() => { setMode("login"); setError(""); setMessage(""); }}>
                  Already have an account? Login
                </Button>
              </>
            )}

            {mode === "verifyOtp" && (
              <>
                <Button onClick={handleVerifyAndRegister} disabled={loading} className="w-100 login-btn mb-2">
                  {loading ? "Verifying…" : "Verify & Create Account"}
                </Button>
                <Button variant="link" className="w-100 text-light text-center" onClick={() => { setMode("register"); setError(""); setMessage(""); }}>
                  Back
                </Button>
              </>
            )}
          </Form>
        </div>
      </div>
    </div>
  );
};

export default EmailLogin;
